import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import {
  BLOCK_META,
  ITEMS_KEY,
  ITEM_CATALOG,
  LEAD_VARIABLES,
  datasourcesOf,
  delayOf,
  itemLabel,
  itemsOf,
  newId,
  newItem,
  setDelay,
} from '../../lib/automation-model';
import {
  connectionsApi,
  customFields,
  listPipelines,
  listStages,
  members,
  products,
  systemParametersApi,
  tags,
} from '../../lib/resources';
import type { AutomationBlock, AutomationBlockItem } from '../../types';
import { Button, compactInputClass, inputClass } from '../ui';

type Options = Record<string, unknown>;

const str = (value: unknown) => (value === null || value === undefined ? '' : String(value));

/** Dados de apoio dos seletores (pipelines e etapas, tags, produtos, membros, conexões, campos). */
function useLookups() {
  const pipelines = useQuery({ queryKey: ['pipelines'], queryFn: listPipelines });
  const stageQueries = useQueries({
    queries: (pipelines.data?.data ?? []).map((pipeline) => ({
      queryKey: ['stages', pipeline.id],
      queryFn: () => listStages(pipeline.id),
    })),
  });
  const tagList = useQuery({
    queryKey: ['tags', 'all'],
    queryFn: () => tags.list({ take: 200 }),
  });
  const productList = useQuery({
    queryKey: ['products', 'all'],
    queryFn: () => products.list({ take: 200 }),
  });
  const memberList = useQuery({
    queryKey: ['members'],
    queryFn: () => members.list(),
    retry: false,
  });
  const connectionList = useQuery({
    queryKey: ['connections'],
    queryFn: connectionsApi.list,
    retry: false,
  });
  const fields = useQuery({
    queryKey: ['custom-fields', 'automation'],
    queryFn: () => customFields.list(),
  });
  const stages = stageQueries.flatMap((query, index) =>
    (query.data ?? []).map((stage) => ({
      ...stage,
      pipelineName: pipelines.data?.data[index]?.name ?? '',
    })),
  );
  return {
    pipelines: pipelines.data?.data ?? [],
    stages,
    tags: tagList.data?.data ?? [],
    products: productList.data?.data ?? [],
    members: memberList.data?.data ?? null,
    connections: connectionList.data?.data ?? null,
    fields: fields.data?.data ?? [],
  };
}

type Lookups = ReturnType<typeof useLookups>;

function Row({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <label className="block text-xs">
      <span className="font-medium text-slate-600">{label}</span>
      {children}
      {hint && <span className="mt-0.5 block text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

/** Campo de texto com o menu "Inserir variável" ({rótulo|caminho}). */
function VariableInput({
  value,
  onChange,
  multiline,
  datasources,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  datasources: string[];
  placeholder?: string;
}) {
  const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const parametersOptions = useQuery({
    queryKey: ['system-parameters'],
    queryFn: systemParametersApi.list,
    retry: false,
  }).data?.data;
  const insert = (token: string) => {
    const element = ref.current;
    const start = element?.selectionStart ?? value.length;
    const end = element?.selectionEnd ?? value.length;
    onChange(`${value.slice(0, start)}${token}${value.slice(end)}`);
  };
  return (
    <div>
      {multiline ? (
        <textarea
          ref={ref}
          className={inputClass}
          rows={4}
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          ref={ref}
          className={inputClass}
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      <select
        className="mt-1 w-full rounded border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-500"
        value=""
        onChange={(event) => {
          if (event.target.value) insert(event.target.value);
        }}
      >
        <option value="">＋ Inserir variável…</option>
        <optgroup label="Lead">
          {LEAD_VARIABLES.map(([path, label]) => (
            <option key={path} value={`{${label}|${path}}`}>
              {label}
            </option>
          ))}
        </optgroup>
        {parametersOptions?.length ? (
          <optgroup label="Parâmetros do sistema">
            {parametersOptions.map((parameter) => (
              <option key={parameter.id} value={`{${parameter.name}|[param]${parameter.name}}`}>
                {parameter.name}
              </option>
            ))}
          </optgroup>
        ) : null}
        {datasources.length > 0 && (
          <optgroup label="Fontes de dados (digite o caminho depois do ])">
            {datasources.map((name) => (
              <option key={name} value={`{campo|[${name}]campo}`}>
                [{name}] campo…
              </option>
            ))}
          </optgroup>
        )}
      </select>
    </div>
  );
}

function StageSelect({
  value,
  onChange,
  lookups,
}: {
  value: string;
  onChange: (value: string) => void;
  lookups: Lookups;
}) {
  return (
    <select
      className={inputClass}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">Escolha a etapa…</option>
      {lookups.pipelines.map((pipeline) => (
        <optgroup key={pipeline.id} label={pipeline.name}>
          {lookups.stages
            .filter((stage) => stage.pipelineId === pipeline.id)
            .map((stage) => (
              <option key={stage.id} value={stage.id}>
                {stage.name}
              </option>
            ))}
        </optgroup>
      ))}
    </select>
  );
}

function PipelineSelect({
  value,
  onChange,
  lookups,
  anyLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  lookups: Lookups;
  anyLabel?: string;
}) {
  return (
    <select
      className={inputClass}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">{anyLabel ?? 'Escolha a pipeline…'}</option>
      {lookups.pipelines.map((pipeline) => (
        <option key={pipeline.id} value={pipeline.id}>
          {pipeline.name}
        </option>
      ))}
    </select>
  );
}

interface ChatButtonOption {
  id?: string;
  text?: string;
  type?: string;
  url?: string;
  nextBlockId?: string;
}

/** Opções de um item, conforme o nome. */
function ItemEditor({
  item,
  onChange,
  lookups,
  datasources,
}: {
  item: AutomationBlockItem;
  onChange: (options: Options) => void;
  lookups: Lookups;
  datasources: string[];
}) {
  const options = (item.options ?? {}) as Options;
  const set = (patch: Options) => onChange({ ...options, ...patch });
  const variable = (key: string, multiline = false, placeholder?: string) => (
    <VariableInput
      value={str(options[key])}
      onChange={(value) => set({ [key]: value })}
      multiline={multiline}
      datasources={datasources}
      placeholder={placeholder}
    />
  );

  switch (item.name) {
    case 'json-http-request-trigger':
      return (
        <Row label="Nome da fonte de dados" hint="O JSON recebido fica em [nome]campo.subcampo">
          <input
            className={inputClass}
            value={str(options.datasourceName)}
            onChange={(event) => set({ datasourceName: event.target.value })}
          />
        </Row>
      );
    case 'message-received-trigger':
      return (
        <div className="space-y-2">
          <Row label="Regra">
            <select
              className={inputClass}
              value={str(options.type) || 'contains'}
              onChange={(event) => set({ type: event.target.value })}
            >
              <option value="contains">Contém</option>
              <option value="equals">É igual a</option>
              <option value="starts-with">Começa com</option>
            </select>
          </Row>
          <Row label="Palavras-chave" hint="Separadas por vírgula. Vazio = qualquer mensagem.">
            <input
              className={inputClass}
              value={((options.keywords as string[]) ?? []).join(', ')}
              onChange={(event) =>
                set({
                  keywords: event.target.value
                    .split(',')
                    .map((keyword) => keyword.trim())
                    .filter(Boolean),
                })
              }
            />
          </Row>
          <Row label="Conexão">
            <select
              className={inputClass}
              value={str(options.instanceId)}
              onChange={(event) => set({ instanceId: event.target.value })}
            >
              <option value="">Qualquer WhatsApp</option>
              {(lookups.connections ?? []).map((connection) => (
                <option key={connection.id} value={connection.id}>
                  {connection.name}
                </option>
              ))}
            </select>
          </Row>
          <Row label="Nova execução">
            <select
              className={inputClass}
              value={str(options.initializeSession) || 'always'}
              onChange={(event) => set({ initializeSession: event.target.value })}
            >
              <option value="always">Sempre</option>
              <option value="only-if-finished">Só se a anterior da conversa terminou</option>
            </select>
          </Row>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={Boolean(options.listenGroup)}
              onChange={(event) => set({ listenGroup: event.target.checked })}
            />
            Ouvir também grupos
          </label>
        </div>
      );
    case 'instagram-comment-received-trigger': {
      const mediaIds = (options.mediaIds as string[] | undefined) ?? [];
      return (
        <div className="space-y-2">
          <Row label="Regra">
            <select
              className={inputClass}
              value={str(options.type) || 'contains'}
              onChange={(event) => set({ type: event.target.value })}
            >
              <option value="contains">Contém</option>
              <option value="equals">É igual a</option>
              <option value="starts-with">Começa com</option>
            </select>
          </Row>
          <Row
            label="Palavras-chave"
            hint="Separadas por vírgula. Vazio = qualquer comentário."
          >
            <input
              className={inputClass}
              value={((options.keywords as string[]) ?? []).join(', ')}
              onChange={(event) =>
                set({
                  keywords: event.target.value
                    .split(',')
                    .map((keyword) => keyword.trim())
                    .filter(Boolean),
                })
              }
            />
          </Row>
          <Row label="Instagram">
            <select
              className={inputClass}
              value={str(options.instanceId)}
              onChange={(event) => set({ instanceId: event.target.value })}
            >
              <option value="">Qualquer Instagram conectado</option>
              {(lookups.connections ?? [])
                .filter((connection) => connection.provider === 'INSTAGRAM_API')
                .map((connection) => (
                  <option key={connection.id} value={connection.id}>
                    {connection.name}
                  </option>
                ))}
            </select>
          </Row>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={Boolean(options.filterMedia)}
              onChange={(event) => set({ filterMedia: event.target.checked })}
            />
            Só em publicações escolhidas
          </label>
          {Boolean(options.filterMedia) && (
            <Row
              label="Ids das publicações"
              hint="Separados por vírgula. Vazio = todas as publicações."
            >
              <input
                className={inputClass}
                value={mediaIds.join(', ')}
                onChange={(event) =>
                  set({
                    mediaIds: event.target.value
                      .split(',')
                      .map((id) => id.trim())
                      .filter(Boolean),
                  })
                }
              />
            </Row>
          )}
          <Row label="Nova execução">
            <select
              className={inputClass}
              value={str(options.initializeSession) || 'always'}
              onChange={(event) => set({ initializeSession: event.target.value })}
            >
              <option value="always">Sempre</option>
              <option value="only-if-finished">Só se a anterior da pessoa terminou</option>
            </select>
          </Row>
        </div>
      );
    }
    case 'business-created-trigger':
    case 'business-won-trigger':
    case 'business-lost-trigger':
      return (
        <Row label="Pipeline">
          <PipelineSelect
            value={str(options.pipelineId)}
            onChange={(pipelineId) => set({ pipelineId })}
            lookups={lookups}
            anyLabel="Qualquer pipeline"
          />
        </Row>
      );
    case 'business-stage-entered-trigger':
    case 'create-business-action':
    case 'move-business-action':
    case 'lead-has-business-on-stage-condition':
      return (
        <Row label="Etapa">
          <StageSelect
            value={str(options.stageId)}
            onChange={(stageId) => set({ stageId })}
            lookups={lookups}
          />
        </Row>
      );
    case 'lead-has-business-on-pipeline-condition':
      return (
        <Row label="Pipeline">
          <PipelineSelect
            value={str(options.pipelineId)}
            onChange={(pipelineId) => set({ pipelineId })}
            lookups={lookups}
          />
        </Row>
      );
    case 'add-attendant-on-business-action':
      return (
        <div className="space-y-2">
          <Row label="Atendente">
            {lookups.members ? (
              <select
                className={inputClass}
                value={str(options.attendantId)}
                onChange={(event) => set({ attendantId: event.target.value })}
              >
                <option value="">Escolha…</option>
                {lookups.members.map((member) => (
                  <option key={member.userId} value={member.userId}>
                    {member.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className={inputClass}
                placeholder="Id do usuário"
                value={str(options.attendantId)}
                onChange={(event) => set({ attendantId: event.target.value })}
              />
            )}
          </Row>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={Boolean(options.addSameAttendantToLead)}
              onChange={(event) => set({ addSameAttendantToLead: event.target.checked })}
            />
            Definir o mesmo atendente no lead
          </label>
        </div>
      );
    case 'distribute-attendant-action': {
      const team = Array.isArray(options.attendantsIds)
        ? (options.attendantsIds as string[])
        : [];
      return (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">
            Vai só para quem da equipe está com "Disponível" ligado, em rodízio. Sem ninguém
            livre, o lead espera na fila (Equipe) e sai em ordem quando alguém ligar.
          </p>
          <Row label="Equipe">
            <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
              {(lookups.members ?? []).map((member) => (
                <label key={member.userId} className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={team.includes(member.userId)}
                    onChange={(event) =>
                      set({
                        attendantsIds: event.target.checked
                          ? [...team, member.userId]
                          : team.filter((id) => id !== member.userId),
                      })
                    }
                  />
                  {member.name}
                </label>
              ))}
            </div>
          </Row>
          <Row label="Máx. de leads sem contato por vendedor">
            <input
              type="number"
              min={1}
              max={20}
              className={inputClass}
              value={Number(options.maxPending ?? 2)}
              onChange={(event) => set({ maxPending: Number(event.target.value) || 1 })}
            />
          </Row>
          <Row label="Prazo do 1º contato (min)">
            <input
              type="number"
              min={0}
              max={1440}
              className={inputClass}
              value={Number(options.slaMinutes ?? 25)}
              onChange={(event) =>
                set({ slaMinutes: Math.max(0, Number(event.target.value) || 0) })
              }
            />
          </Row>
          <p className="text-[11px] text-slate-400">
            Passado o prazo sem contato (clique no WhatsApp ou mudança de etapa), o vendedor é
            marcado como indisponível e o lead vai para outro. 0 = sem prazo.
          </p>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={options.addSameAttendantToLead !== false}
              onChange={(event) => set({ addSameAttendantToLead: event.target.checked })}
            />
            Definir o mesmo atendente no lead
          </label>
        </div>
      );
    }
    case 'add-product-on-business-action':
      return (
        <div className="space-y-2">
          <Row label="Produto">
            <select
              className={inputClass}
              value={str(options.productId)}
              onChange={(event) => set({ productId: event.target.value, productSku: '' })}
            >
              <option value="">Escolha…</option>
              {lookups.products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </Row>
          <div className="grid grid-cols-2 gap-2">
            <Row label="Preço (R$)" hint="Vazio = preço do produto">
              {variable('price', false, 'ex.: 197,00')}
            </Row>
            <Row label="Quantidade">
              <input
                className={inputClass}
                inputMode="numeric"
                value={str(options.quantity)}
                placeholder="1"
                onChange={(event) => set({ quantity: event.target.value })}
              />
            </Row>
          </div>
        </div>
      );
    case 'add-tag-action': {
      const selected = new Set((options.tagIds as string[]) ?? []);
      return (
        <div className="space-y-2">
          <Row label="Tags">
            <div className="mt-1 max-h-36 space-y-0.5 overflow-y-auto rounded border border-slate-200 p-2">
              {lookups.tags.map((tag) => (
                <label key={tag.id} className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={selected.has(tag.id)}
                    onChange={(event) => {
                      const next = new Set(selected);
                      if (event.target.checked) next.add(tag.id);
                      else next.delete(tag.id);
                      set({ tagIds: [...next] });
                    }}
                  />
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: tag.color ?? '#94A3B8' }}
                  />
                  {tag.name}
                </label>
              ))}
            </div>
          </Row>
          <Row label="Ou pelo nome" hint="Cria a tag se não existir. Aceita variável.">
            {variable('tagName')}
          </Row>
        </div>
      );
    }
    case 'create-tag-action':
      return (
        <div className="space-y-2">
          <Row label="Nome">{variable('name')}</Row>
          <Row label="Cor">
            <input
              type="color"
              className="mt-1 h-8 w-16 rounded border border-slate-200"
              value={str(options.color) || '#94A3B8'}
              onChange={(event) => set({ color: event.target.value })}
            />
          </Row>
          <Row label="Descrição">
            <input
              className={inputClass}
              value={str(options.description)}
              onChange={(event) => set({ description: event.target.value })}
            />
          </Row>
        </div>
      );
    case 'lead-with-phone-exists-condition':
      return <Row label="Telefone">{variable('phone')}</Row>;
    case 'set-field-operation': {
      const parameter = str(options.parameter);
      const known = [
        ['leadName', 'Nome do lead'],
        ['leadPhone', 'Telefone do lead'],
        ['leadEmail', 'E-mail do lead'],
        ['leadSource', 'Origem do lead'],
        ...lookups.fields.map((field) => [
          `additional-field[${field.name}]`,
          `${field.entity === 'business' ? 'Negócio' : 'Lead'} · ${field.name}`,
        ]),
      ];
      const isKnown = known.some(([value]) => value === parameter);
      return (
        <div className="space-y-2">
          <Row label="Campo">
            <select
              className={inputClass}
              value={isKnown ? parameter : '__other__'}
              onChange={(event) =>
                set({
                  parameter:
                    event.target.value === '__other__' ? '[dados]campo' : event.target.value,
                })
              }
            >
              {known.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
              <option value="__other__">
                Outro (fonte de dados ou campo que não existe aqui)…
              </option>
            </select>
          </Row>
          {!isKnown && (
            <Row label="Parâmetro" hint="[fonte]campo grava numa fonte de dados">
              <input
                className={inputClass}
                value={parameter}
                onChange={(event) => set({ parameter: event.target.value })}
              />
            </Row>
          )}
          <Row label="Valor">{variable('value')}</Row>
        </div>
      );
    }
    case 'parse-phone-field-operation':
      return (
        <div className="space-y-2">
          <Row label="Telefone">{variable('phone')}</Row>
          <div className="grid grid-cols-2 gap-2">
            <Row label="Fonte de saída" hint="Use [nome]phone">
              <input
                className={inputClass}
                value={str(options.datasourceName)}
                onChange={(event) => set({ datasourceName: event.target.value })}
              />
            </Row>
            <Row label="País padrão">
              <input
                className={inputClass}
                maxLength={2}
                value={str(options.defaultCountry)}
                onChange={(event) => set({ defaultCountry: event.target.value.toUpperCase() })}
              />
            </Row>
          </div>
        </div>
      );
    case 'send-text-message': {
      const buttons = (options.buttons as ChatButtonOption[] | undefined) ?? [];
      const setButtons = (next: ChatButtonOption[]) => set({ buttons: next });
      return (
        <div className="space-y-2">
          <Row
            label="Texto"
            hint="No Instagram, envia para quem comentou ou mandou DM no gatilho. No WhatsApp o envio está desligado: só registra."
          >
            {variable('text', true)}
          </Row>
          <Row
            label="Botões (Instagram)"
            hint="Resposta: cada botão vira uma saída do bloco no canvas. Link: abre o endereço. Até 3 com link, 13 sem."
          >
            <div className="space-y-2">
              {buttons.map((button, index) => (
                <div
                  key={button.id ?? index}
                  className="space-y-1 rounded border border-slate-200 p-2"
                >
                  <div className="flex gap-2">
                    <input
                      className={inputClass}
                      placeholder="Texto do botão (até 20 letras)"
                      value={button.text ?? ''}
                      onChange={(event) =>
                        setButtons(
                          buttons.map((item, i) =>
                            i === index ? { ...item, text: event.target.value } : item,
                          ),
                        )
                      }
                    />
                    <select
                      className={inputClass}
                      value={button.type === 'link' ? 'link' : 'button'}
                      onChange={(event) =>
                        setButtons(
                          buttons.map((item, i) =>
                            i === index ? { ...item, type: event.target.value } : item,
                          ),
                        )
                      }
                    >
                      <option value="button">Resposta</option>
                      <option value="link">Link</option>
                    </select>
                    <button
                      type="button"
                      className="text-xs text-red-600"
                      onClick={() => setButtons(buttons.filter((_, i) => i !== index))}
                    >
                      Remover
                    </button>
                  </div>
                  {button.type === 'link' && (
                    <input
                      className={inputClass}
                      placeholder="https://…"
                      value={button.url ?? ''}
                      onChange={(event) =>
                        setButtons(
                          buttons.map((item, i) =>
                            i === index ? { ...item, url: event.target.value } : item,
                          ),
                        )
                      }
                    />
                  )}
                </div>
              ))}
              <button
                type="button"
                className="text-xs font-medium text-indigo-600"
                onClick={() =>
                  setButtons([
                    ...buttons,
                    { id: newId(), text: '', type: 'button', nextBlockId: '' },
                  ])
                }
              >
                + Adicionar botão
              </button>
            </div>
          </Row>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={Boolean(options.breakMessages)}
              onChange={(event) => set({ breakMessages: event.target.checked })}
            />
            Quebrar em várias mensagens (um parágrafo por mensagem)
          </label>
        </div>
      );
    }
    case 'send-comment-instagram':
      return (
        <div className="space-y-2">
          <Row label="Resposta" hint="Responde o comentário que disparou a automação.">
            {variable('text', true)}
          </Row>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={Boolean(options.privateReply)}
              onChange={(event) => set({ privateReply: event.target.checked })}
            />
            Responder no direct (resposta privada) em vez de na publicação
          </label>
        </div>
      );
    case 'instagram-follower':
      return (
        <p className="text-xs text-slate-500">
          Verdadeiro se quem comentou ou mandou DM segue a conta do Instagram.
        </p>
      );
    case 'delay-message':
      return (
        <Row label="Segundos">
          <input
            type="number"
            min={0}
            className={inputClass}
            value={str(options.seconds)}
            onChange={(event) => set({ seconds: Number(event.target.value) })}
          />
        </Row>
      );
    case 'create-lead-action':
      return (
        <p className="text-xs text-slate-500">
          Cria o lead com os campos definidos antes (nome, telefone, e-mail, origem). Se já
          existir um lead com o mesmo telefone ou e-mail, ele é reaproveitado.
        </p>
      );
    case 'win-business-action':
      return (
        <p className="text-xs text-slate-500">Marca como ganho o negócio atual do fluxo.</p>
      );
    case 'lead-exists-condition':
      return (
        <p className="text-xs text-slate-500">
          Verdadeiro se o fluxo já tem lead ou se existe um com o telefone/e-mail definidos.
        </p>
      );
    default:
      return (
        <p className="text-xs text-amber-700">
          Item sem canal neste CRM — fica guardado como veio e não executa.
        </p>
      );
  }
}

/** Painel lateral do bloco selecionado. */
export function BlockPanel({
  block,
  blocks,
  canEdit,
  onChange,
  onDelete,
  onDuplicate,
  counters,
}: {
  block: AutomationBlock;
  blocks: AutomationBlock[];
  canEdit: boolean;
  onChange: (block: AutomationBlock) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  counters?: { success: number; alert: number; error: number };
}) {
  const lookups = useLookups();
  const datasources = useMemo(() => datasourcesOf(blocks), [blocks]);
  const meta = BLOCK_META[block.type];
  const key = ITEMS_KEY[block.type];
  const items = itemsOf(block);
  const options = block.options ?? {};

  const setOptions = (patch: Options) =>
    onChange({ ...block, options: { ...options, ...patch } });
  const setItems = (next: AutomationBlockItem[]) => key && setOptions({ [key]: next });

  return (
    <fieldset disabled={!canEdit} className="flex h-full flex-col">
      <header className="border-b border-slate-200 p-4">
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: meta.color }} />
          <h2 className="font-semibold text-slate-900">{meta.label}</h2>
        </div>
        <p className="mt-0.5 text-xs text-slate-500">{meta.hint}</p>
        {counters && (
          <p className="mt-2 text-xs text-slate-500">
            <span className="text-emerald-600">{counters.success} ok</span> ·{' '}
            <span className="text-amber-600">{counters.alert} alertas</span> ·{' '}
            <span className="text-red-600">{counters.error} erros</span>
          </p>
        )}
      </header>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {key &&
          items.map((item, index) => (
            <div
              key={String(item.stepId ?? `${item.name}-${index}`)}
              className="rounded-lg border border-slate-200 p-3"
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-slate-800">
                  {itemLabel(item.name)}
                </span>
                <div className="flex shrink-0 gap-1 text-xs text-slate-400">
                  <button
                    type="button"
                    disabled={index === 0}
                    className="px-1 hover:text-slate-700 disabled:opacity-30"
                    title="Subir"
                    onClick={() => {
                      const next = [...items];
                      [next[index - 1], next[index]] = [next[index], next[index - 1]];
                      setItems(next);
                    }}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    disabled={index === items.length - 1}
                    className="px-1 hover:text-slate-700 disabled:opacity-30"
                    title="Descer"
                    onClick={() => {
                      const next = [...items];
                      [next[index + 1], next[index]] = [next[index], next[index + 1]];
                      setItems(next);
                    }}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="px-1 hover:text-red-600"
                    title="Remover"
                    onClick={() => setItems(items.filter((_, position) => position !== index))}
                  >
                    ✕
                  </button>
                </div>
              </div>
              <ItemEditor
                item={item}
                lookups={lookups}
                datasources={datasources}
                onChange={(next) =>
                  setItems(
                    items.map((entry, position) =>
                      position === index ? { ...entry, options: next } : entry,
                    ),
                  )
                }
              />
            </div>
          ))}

        {key && (
          <select
            className={`${compactInputClass} w-full`}
            value=""
            onChange={(event) => {
              if (event.target.value) setItems([...items, newItem(event.target.value)]);
            }}
          >
            <option value="">
              ＋ Adicionar {block.type === 'condition' ? 'condição' : 'item'}…
            </option>
            {ITEM_CATALOG[key]
              .filter((entry) => !entry.legacy)
              .map((entry) => (
                <option key={entry.name} value={entry.name}>
                  {entry.label}
                </option>
              ))}
          </select>
        )}
        {block.type === 'condition' && items.length > 1 && (
          <p className="text-[11px] text-slate-400">
            Todas as condições precisam ser verdadeiras.
          </p>
        )}

        {block.type === 'delay' && <DelayEditor block={block} onChange={onChange} />}
        {block.type === 'randomizer' && <RandomizerEditor block={block} onChange={onChange} />}
        {block.type === 'api' && (
          <ApiEditor options={options} setOptions={setOptions} datasources={datasources} />
        )}

        <p className="text-[11px] text-slate-400">
          Ligue os blocos arrastando do ponto à direita do cartão até o próximo bloco.
        </p>
      </div>

      {canEdit && (
        <footer className="flex justify-between gap-2 border-t border-slate-200 p-3">
          <Button onClick={onDuplicate}>Duplicar</Button>
          <Button variant="danger" onClick={onDelete}>
            Excluir bloco
          </Button>
        </footer>
      )}
    </fieldset>
  );
}

function DelayEditor({
  block,
  onChange,
}: {
  block: AutomationBlock;
  onChange: (block: AutomationBlock) => void;
}) {
  const { unit, amount } = delayOf(block);
  return (
    <div className="grid grid-cols-2 gap-2">
      <Row label="Quanto">
        <input
          type="number"
          min={0}
          className={inputClass}
          value={amount}
          onChange={(event) => onChange(setDelay(block, unit, Number(event.target.value)))}
        />
      </Row>
      <Row label="Unidade">
        <select
          className={inputClass}
          value={unit}
          onChange={(event) => onChange(setDelay(block, event.target.value, amount))}
        >
          <option value="seconds-delay">Segundos</option>
          <option value="hours-delay">Horas</option>
          <option value="days-delay">Dias</option>
        </select>
      </Row>
    </div>
  );
}

function RandomizerEditor({
  block,
  onChange,
}: {
  block: AutomationBlock;
  onChange: (block: AutomationBlock) => void;
}) {
  const paths = (block.options?.randomizers ?? []) as Array<{
    id?: string;
    name?: string;
    perc?: number;
    nextBlockId?: string;
  }>;
  const total = paths.reduce((sum, path) => sum + Number(path.perc || 0), 0);
  const save = (next: typeof paths) =>
    onChange({ ...block, options: { ...block.options, randomizers: next } });
  return (
    <div className="space-y-2">
      {paths.map((path, index) => (
        <div key={path.id ?? index} className="flex items-center gap-2">
          <input
            className={`${compactInputClass} w-20`}
            value={path.name ?? ''}
            onChange={(event) =>
              save(
                paths.map((entry, position) =>
                  position === index ? { ...entry, name: event.target.value } : entry,
                ),
              )
            }
          />
          <input
            type="number"
            min={0}
            max={100}
            className={`${compactInputClass} w-20`}
            value={path.perc ?? 0}
            onChange={(event) =>
              save(
                paths.map((entry, position) =>
                  position === index ? { ...entry, perc: Number(event.target.value) } : entry,
                ),
              )
            }
          />
          <span className="text-xs text-slate-400">%</span>
          <button
            type="button"
            className="ml-auto text-xs text-slate-400 hover:text-red-600"
            disabled={paths.length <= 2}
            onClick={() => save(paths.filter((_, position) => position !== index))}
          >
            ✕
          </button>
        </div>
      ))}
      <div className="flex items-center justify-between">
        <Button
          onClick={() =>
            save([
              ...paths,
              {
                id: newId(),
                name: String.fromCharCode(65 + paths.length),
                perc: 0,
                nextBlockId: '',
              },
            ])
          }
        >
          ＋ Caminho
        </Button>
        <span
          className={`text-xs ${Math.round(total) === 100 ? 'text-emerald-600' : 'text-red-600'}`}
        >
          Total {total}% {Math.round(total) === 100 ? '' : '(precisa somar 100%)'}
        </span>
      </div>
    </div>
  );
}

function ApiEditor({
  options,
  setOptions,
  datasources,
}: {
  options: Options;
  setOptions: (patch: Options) => void;
  datasources: string[];
}) {
  const headers = (options.headers ?? []) as Array<{ key?: string; value?: string }>;
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[90px_1fr] gap-2">
        <Row label="Método">
          <select
            className={inputClass}
            value={str(options.method) || 'POST'}
            onChange={(event) => setOptions({ method: event.target.value })}
          >
            {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((method) => (
              <option key={method}>{method}</option>
            ))}
          </select>
        </Row>
        <Row label="URL">
          <VariableInput
            value={str(options.url)}
            onChange={(url) => setOptions({ url })}
            datasources={datasources}
            placeholder="https://"
          />
        </Row>
      </div>
      <Row label="Cabeçalhos">
        <div className="mt-1 space-y-1">
          {headers.map((header, index) => (
            <div key={index} className="flex gap-1">
              <input
                className={`${compactInputClass} w-28`}
                placeholder="Nome"
                value={header.key ?? ''}
                onChange={(event) =>
                  setOptions({
                    headers: headers.map((entry, position) =>
                      position === index ? { ...entry, key: event.target.value } : entry,
                    ),
                  })
                }
              />
              <input
                className={`${compactInputClass} flex-1`}
                placeholder="Valor"
                value={header.value ?? ''}
                onChange={(event) =>
                  setOptions({
                    headers: headers.map((entry, position) =>
                      position === index ? { ...entry, value: event.target.value } : entry,
                    ),
                  })
                }
              />
              <button
                type="button"
                className="px-1 text-xs text-slate-400 hover:text-red-600"
                onClick={() =>
                  setOptions({ headers: headers.filter((_, position) => position !== index) })
                }
              >
                ✕
              </button>
            </div>
          ))}
          <Button onClick={() => setOptions({ headers: [...headers, { key: '', value: '' }] })}>
            ＋ Cabeçalho
          </Button>
        </div>
      </Row>
      <Row label="Corpo (JSON)">
        <VariableInput
          value={str(options.body)}
          onChange={(body) => setOptions({ body })}
          datasources={datasources}
          multiline
        />
      </Row>
      <Row label="Fonte de saída" hint="A resposta fica em [nome]data.campo e [nome]status">
        <input
          className={inputClass}
          value={str(options.datasourceName)}
          onChange={(event) => setOptions({ datasourceName: event.target.value })}
        />
      </Row>
      <p className="text-[11px] text-slate-400">
        Nunca chama endereços internos. Tempo máximo de 15 s; resposta até 1 MB. Resposta fora
        de 2xx segue pela saída "Se der erro".
      </p>
    </div>
  );
}
